import React, { useState } from "react";
import { Drawer, message } from "antd";
import Form from "../../Form";
import styles from "./TicketUploadDrawer.module.scss";
import {
  DrawerConstants,
  DrawerHeaderStyle,
  TicketNotificationMessages,
  ticketUploadConstants,
  uploadTicketDrawerFormValidation,
} from "./constants";
import AddTicketForm from "./AddTicketForm";
import TNButton from "../../../reusable-components/TNButton";
import { ButtonShape, ButtonSize } from "../../../enums/ButtonAttributesEnums";
import clsx from "clsx";
import TicketUploadServices from "../../../services/ticketUpload.service";
import { useHistory } from "react-router-dom";
import { appRoutes } from "../../../constants/appRoutes";
import TNLoader from "../../../reusable-components/TNLoader";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../../../constants/QueryKeys";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import { ticketUploadDefaultValue } from "./AddTicketForm/constants";

export interface TicketUploadDrawerProps {
  isOpen: boolean;
  handleClose?: () => void;
  refetchTickets?: () => void;
  allowReview?: boolean;
}

const TicketUploadDrawer = (props: TicketUploadDrawerProps) => {
  const navigate = useHistory();
  const queryClient = useQueryClient();
  const { isOpen, handleClose, refetchTickets, allowReview } = props;
  const handleDrawerClose = (reset) => {
    handleClose();
    reset();
  };

  const [uploadTicketPayload, setUploadTicketPayload] = useState({});
  const { SaveTicket } = TicketUploadServices();
  const { mutateAsync: saveTicket, isPending: isSaveTicketPending } =
    SaveTicket();

  const handleSaveTicket = async (reset, redirect = false) => {
    await saveTicket(uploadTicketPayload, {
      onSuccess: (data) => {
        message.success(TicketNotificationMessages.success);
        if (redirect) {
          navigate.push(
            `${appRoutes.VIEW_TICKET_UPLOAD_REVIEW}${data?.data?.id}`,
          );
        } else {
          reset();
          handleClose();
          refetchTickets();
        }
        setUploadTicketPayload({});
        queryClient.invalidateQueries(QueryKeys.FETCH_TICKET_FILTERS_KEY);
      },
      onError: (e) => {
        message.error(TicketNotificationMessages.failure);
      },
    });
  };

  const DrawerHeader = (
    <div className={styles.DrawerHeaderContainer}>
      <p>{ticketUploadConstants.HEADER_TXT}</p>
    </div>
  );

  const DrawerFooter = ({ handleSubmit, reset }) => {
    return (
      <div className={styles.DrawerFooterContainer}>
        <ConditionalRender visible={allowReview}>
          <TNButton
            className={clsx(ticketUploadConstants.SAVE_REVIEW_BTN_CLASS, {
              [ticketUploadConstants.DISABLED_BTN_CLASS]:
                uploadTicketPayload?.ticketUploadPicture === undefined,
            })}
            shape={ButtonShape.DEFAULT}
            size={ButtonSize.LARGE}
            text={ticketUploadConstants.SAVE_REVIEW_BTN_TEXT}
            disabled={uploadTicketPayload?.ticketUploadPicture === undefined}
            onClick={handleSubmit(() => handleSaveTicket(reset, true))}
          />
        </ConditionalRender>
        <TNButton
          className={clsx({
            [ticketUploadConstants.DISABLED_BTN_CLASS]:
              uploadTicketPayload?.ticketUploadPicture === undefined,
          })}
          shape={ButtonShape.DEFAULT}
          size={ButtonSize.LARGE}
          text={ticketUploadConstants.SAVE_BTN_TXT}
          disabled={uploadTicketPayload?.ticketUploadPicture === undefined}
          onClick={handleSubmit(() => handleSaveTicket(reset))}
        />
      </div>
    );
  };
  return (
    <>
      <TNLoader isLoading={isSaveTicketPending} />
      <Form
        validationSchema={uploadTicketDrawerFormValidation}
        defaultValues={ticketUploadDefaultValue}
      >
        {({ reset, control: { handleSubmit } }) => {
          return (
            <Drawer
              visible={isOpen}
              maskClosable={false}
              destroyOnClose
              className={styles.UploadTicketDrawer}
              closable={true}
              mask={true}
              getContainer={false}
              onClose={() => handleDrawerClose(reset)}
              placement={DrawerConstants.DRAWER_PLACEMENT}
              width={DrawerConstants.DRAWER_WIDTH}
              height={DrawerConstants.DRAWER_HEIGHT}
              size={DrawerConstants.DRAWER_SIZE}
              title={DrawerHeader}
              footer={DrawerFooter({ handleSubmit, reset })}
              headerStyle={DrawerHeaderStyle}
            >
              <AddTicketForm
                setUploadTicketPayload={setUploadTicketPayload}
                isOpen={isOpen}
              />
            </Drawer>
          );
        }}
      </Form>
    </>
  );
};

export default TicketUploadDrawer;
